import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';

import { TranslateModule } from '@ngx-translate/core';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';

import { Subject, takeUntil } from 'rxjs';

import { ESvgIcons } from 'src/app/core/enums/svg-icons';
import { SwiperOptions } from 'swiper/types/swiper-options';
import { EInfoType } from 'src/app/core/enums/country-info-type.enum';

import { SwiperDirective } from '../../directives/swiper.directive';

import { TripsService } from 'src/app/core/services/trips.service';
import { SsrHelperService } from 'src/app/core/services/ssr-helper.service';
import { CloudinaryService } from 'src/app/core/services/cloudinary.service';

import { PopularTripDialogComponent } from '../../dialogs/popular-trip-dialog/popular-trip-dialog.component';

import { Virtual } from 'swiper/modules';

@Component({
  selector: 'app-popular-itineraries',
  templateUrl: './popular-itineraries.component.html',
  styleUrls: ['./popular-itineraries.component.scss'],
  imports: [
    SwiperDirective,
    TranslateModule,
    MatIconModule,
    NgxSkeletonLoaderModule,
  ],
})
export class PopularItinerariesComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() customTrips: Array<any> = [];
  @Input() infoType: EInfoType | null = null;
  @Input() isFetching: boolean = false;
  @Input() isHeader: boolean = true;
  @Input() swiperSlideClass: string[] = [];
  @Input() swiperWrapperClass: string[] = [];
  @Input() showBrowseButton: boolean = false;

  @Output() browse: EventEmitter<void> = new EventEmitter<void>();

  public trips: Array<any> = [];
  public currentIndex = 0;
  public isFirstSlide = true;
  public isLastSlide = false;
  public fetchingTrips: number[] = new Array(6);

  public readonly eSvgIcons = ESvgIcons;

  swiperOptions: SwiperOptions = {
    modules: [Virtual],
    lazyPreloadPrevNext: 2,
    autoplay: {
      delay: 8_000,
    },
    speed: 400,
    slidesPerView: 'auto',
    spaceBetween: 8,
    loop: true,
    breakpoints: {
      320: {
        slidesPerView: 'auto',
        spaceBetween: 8,
      },
    },
    on: {
      slideChange: (swiper) => {
        if (swiper) {
          this.currentIndex = swiper.activeIndex;
        }
      },
    },
  };

  @ViewChild(SwiperDirective) swiper: SwiperDirective;

  readonly #matDialog: MatDialog = inject(MatDialog);
  readonly #ssrHelperService: SsrHelperService = inject(SsrHelperService);

  private readonly destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private tripsService: TripsService,
    private cloudinaryService: CloudinaryService,
  ) {}

  get isCustomTrips(): boolean {
    return this.infoType !== null;
  }

  ngOnInit() {
    this.getTopTrips();
  }

  ngOnChanges(changes: SimpleChanges) {
    const { customTrips } = changes;
    if (this.#ssrHelperService.userAgent && customTrips?.currentValue?.length) {
      this.getTopTrips();

      if (this.swiper) {
        this.swiper.updateSwiper();
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  public openTripOverview(topTrip: any): void {
    const { tripData } = topTrip;
    const openDialogWithAdvanceInfo = (trip: any) => {
      this.#matDialog.open(PopularTripDialogComponent, {
        width: '1324px',
        maxWidth: '96vw',
        data: {
          trip: trip,
        },
      });
    };

    if (!tripData) {
      this.tripsService.getTripByUrlName(topTrip.url).subscribe((tripData) => {
        if (tripData) {
          const trip = { ...topTrip, tripData: tripData };
          openDialogWithAdvanceInfo(trip);
        }
      });
    } else {
      openDialogWithAdvanceInfo(topTrip);
    }
  }

  public ignoreLink(ev: MouseEvent): boolean {
    ev.preventDefault();
    return false;
  }

  public scrollLeft(): void {
    if (this.swiper) {
      this.swiper.swiperInstance.slidePrev();
    }
  }

  public scrollRight(): void {
    if (this.swiper) {
      this.swiper.swiperInstance.slideNext();
    }
  }

  private getTopTrips(): void {
    if (!this.isCustomTrips) {
      this.tripsService
        .getHomepageTopItineraries()
        .pipe(takeUntil(this.destroy$))
        .subscribe((data) => this.groupImageAndTrip(data));
    } else {
      const slicedCustomTrips: any[] = this.customTrips.slice(0, 6);
      this.groupImageAndTrip(slicedCustomTrips);
    }
  }

  private groupImageAndTrip(data: any[]): void {
    if (data && data.length) {
      this.trips = data;
      for (let i = 0; i < this.trips.length; i++) {
        if (this.trips[i].image_url) {
          this.trips[i].image_url = this.cloudinaryService.getCloudImage(
            this.trips[i].image_url,
          );
        }
      }
    }
  }
}
